import {
    createEntityAdapter,
    EntityAdapter,
    EntityState
} from '@ngrx/entity';
import {
    Action,
    createReducer,
    on
} from '@ngrx/store';

import { DatiqUserPermissions } from '../models';
import { IDatiqUser } from '../models/datiq-user.interface';
import * as DatiqUserActions from './datiq-user.actions';
import * as DatiqUserReducerUtils from './datiq-user.reducer-utils';

export const DATIQ_USER_FEATURE_KEY = 'datiqUser';

const testPermissions: string[] = [
    DatiqUserPermissions.EnableRateForecastFiftyTwoWeeks, // Turn on for Rate Cast
    DatiqUserPermissions.EnableMarketConditionsGauge, // Turn on for MC
    DatiqUserPermissions.EnableMarketConditionsForecast, // Turn on for MC Forecast
    DatiqUserPermissions.HotMarketMapsEnable, // Turn on for MC
    DatiqUserPermissions.ContractRateHistoricLookup, // Turn on for Rateview
    DatiqUserPermissions.ClientEnableFreightMatching, // Turn on for Power
];

export interface State extends EntityState<IDatiqUser> {
    selectedId?: string; // which DatiqUser record has been selected
    loaded: boolean; // has the DatiqUser list been loaded
    error?: string | null; // last known error (if any)
}

export interface DatiqUserPartialState {
    readonly [DATIQ_USER_FEATURE_KEY]: State;
}

export const datiqUserAdapter: EntityAdapter<IDatiqUser> =
    createEntityAdapter<IDatiqUser>();

export const initialState: State = datiqUserAdapter.getInitialState({
    // set initial required properties
    loaded: false,
});

const datiqUserReducer = createReducer(
    initialState,
    on(DatiqUserActions.login, (state) => ({
        ...state,
        loaded: false,
        error: null,
    })),
    on(DatiqUserActions.loginSuccess, (state, { user }) =>
        datiqUserAdapter.setOne(
            {
                ...user,
                authorizations: {
                    ...user?.authorizations,
                    permissions: [
                        /**
                         * To test permissions comment out user permissions
                         * and uncomment out the test permissions line then
                         * go to the array and add the permissions you wish to change.
                         *
                         * !REMEMBER TO REVERT THE CHANGES!!!!!!
                         */
                        ...(user?.authorizations?.permissions ?? []),
                        //...testPermissions,
                    ],
                },
            },
            {
                ...state,
                selectedId: user.id,
                loaded: true,
            }
        )
    ),
    on(DatiqUserActions.loginFail, (state, { error }) => ({
        ...state,
        error,
    })),
    on(DatiqUserActions.updateUserCache, (state, { userUiState }) =>
        datiqUserAdapter.updateOne(
            {
                id: state.selectedId,
                changes: {
                    uiStates: userUiState,
                },
            },
            state
        )
    ),
    on(DatiqUserActions.cacheUI, (state, { uiId }) =>
        datiqUserAdapter.updateOne(
            {
                id: state.selectedId,
                changes: {
                    uiStates: {
                        ...state?.entities?.[state.selectedId]?.uiStates,
                        loaded: false,
                    },
                },
            },
            state
        )
    ),
    on(DatiqUserActions.viewModal, (state, { modalId }) => {
        return datiqUserAdapter.updateOne(
            DatiqUserReducerUtils.updateViewModalStateToUser(modalId, state),
            state
        );
    }),
    on(DatiqUserActions.showAgain, (state, { modalId }) => {
        return datiqUserAdapter.updateOne(
            DatiqUserReducerUtils.updateShowAgainModalStateToUser(
                modalId,
                state
            ),
            state
        );
    }),
    on(DatiqUserActions.updateUserResources, (state, { resources }) => datiqUserAdapter.updateOne(
        {
            id: state.selectedId,
            changes: {
                resources
            }
        },
        state
    )),
);

export function reducer(state: State | undefined, action: Action) {
    return datiqUserReducer(state, action);
}
